import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CreditPlansCodes, Personality } from 'src/app/@core/constants';
import { PosUpdateRtnFiscalIdModalService } from 'src/app/@core/services/pos-update-rtn-fiscal-id-modal.service';
import { Customer } from 'src/app/models/customer';
import { OnbaseProcedureResponseModel } from 'src/app/models/response/onbaseProcedureResponseModel';
import { CustomerTableModel } from 'src/app/models/table-models/customer-table';
import { User } from 'src/app/models/user';
import Swal from 'sweetalert2';
import { CustomerValidationsService } from '../../services/customer-validations.service';
import { CreditStatusFranchisedResponseModel } from './../../../../models/response/customerCreditStatusResponseModel';
import { OnbaseService } from './../../../../services/onbase.service';

@Component({
  selector: 'app-fast-actions-buttons',
  templateUrl: './fast-actions-buttons.component.html',
  styleUrls: ['./fast-actions-buttons.component.sass'],
})
export class FastActionsButtonsComponent implements OnInit {
  @Input()
  currentCustomerCreditConditions: CreditStatusFranchisedResponseModel =
    new CreditStatusFranchisedResponseModel();
  @Input() currentCustomer: Customer = new Customer();
  @Input() selectedSeller: CustomerTableModel | User | undefined;
  creditPlans!: Array<OnbaseProcedureResponseModel>;

  private delayedAmountByContract: any[] = [];
  private readonly CVN_CREDIT_PLAN_CODE = '0008';

  constructor(
    private router: Router,
    private onbaseService: OnbaseService,
    private toastr: ToastrService,
    private customerValidationsService: CustomerValidationsService,
    private rtnFiscalIdService: PosUpdateRtnFiscalIdModalService,
    private customerValidationService: CustomerValidationsService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.fetchCreditPlans();
  }

  private async fetchCreditPlans(): Promise<void> {
    this.creditPlans = (
      await this.onbaseService.readCreditPlans().toPromise()
    ).response.content;
  }

  private getCreditPlanByCode(
    code: CreditPlansCodes
  ): OnbaseProcedureResponseModel {
    return (
      this.creditPlans.find((c) => c.code === code) ||
      new OnbaseProcedureResponseModel()
    );
  }

  async handlePacClick(): Promise<void> {
    this.redirectToCreateCreditRequest(CreditPlansCodes.PAC);
  }

  async handleCvnClick(): Promise<void> {
    this.redirectToCreateCreditRequest(CreditPlansCodes.CVN);
  }

  async handle30daysClick(): Promise<void> {
    this.redirectToCreateCreditRequest(CreditPlansCodes.THIRTYDAYS);
  }

  async handleRaiseLimitClick(): Promise<void> {
    if (
      this.currentCustomer.mainPersonalInfo?.personality ===
      Personality.LEGAL_ENTITY
    ) {
      const continueToCreate =
        await this.customerValidationsService.validateCustomerInDefault(
          this.currentCustomer.code
        );
      if (continueToCreate) {
        this.redirectToCreateCreditRequest(CreditPlansCodes.JUR);
      }
    }

    if (
      this.currentCustomer.mainPersonalInfo?.personality === Personality.PERSON
    ) {
      const continueToCreate =
        await this.customerValidationsService.validateCustomerInDefault(
          this.currentCustomer.code
        );
      if (continueToCreate) {
        this.redirectToCreateCreditRequest(CreditPlansCodes.NOR);
      }
    }
  }

  async handleInvoiceClick(): Promise<void> {
    let employee = new CustomerTableModel();
    employee.code = (this.selectedSeller as User).username;
    employee.fullName = (this.selectedSeller as User).fullname;

    this.router.navigate(['sales'], {
      state: {
        customer: this.currentCustomer,
        employee: employee
      },
      replaceUrl: true,
    });
  }

  private async redirectToCreateCreditRequest(
    creditPlanCode: CreditPlansCodes
  ): Promise<void> {
    const result = await this.customerValidationService.validateOnBaseCreditStatus(this.currentCustomer.code);
    if (result) {
      this.router.navigate(['creditRequests/create'], {
        state: {
          creditPlan: this.getCreditPlanByCode(creditPlanCode),
          customer: this.currentCustomer,
          customerCreditConditions: this.currentCustomerCreditConditions,
          seller: this.selectedSeller
        },
        replaceUrl: true,
      });
    }
  }

  toAdvance() {
    this.router.navigate(['customer/advances', this.currentCustomer.code]);
  }

  async updateRtnFiscalId() {
    let customerTableModel = new CustomerTableModel();
    customerTableModel.code = this.currentCustomer.code;
    customerTableModel.personality =
      this.currentCustomer.mainPersonalInfo.personality;
    customerTableModel.fiscalId =
      this.currentCustomer.mainPersonalInfo.fiscalId;
    customerTableModel.fiscalId2 =
      this.currentCustomer.mainPersonalInfo.fiscalId2;

    await this.rtnFiscalIdService.show(
      customerTableModel,
      (customer: CustomerTableModel) => {
        Swal.fire({
          title: `Actualización de RTN`,
          html: 'Operación Exitosa. </br> RTN actualizado con éxito',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
      }
    );
  }

  async handleQuotationCvnClick() {
    let employee = new CustomerTableModel();
    employee.code = (this.selectedSeller as User).username;
    employee.fullName = (this.selectedSeller as User).fullname;

    const creditPlan = (await this.onbaseService.readCreditPlans().toPromise()).response.content.find(p => p.code === this.CVN_CREDIT_PLAN_CODE);
    this.router.navigate(['sales'], {
      state: {
        creditPlan,
        customer: this.currentCustomer,
        employee: employee
      }
    });
  }

  showConvenio() {
    return this.currentCustomer.mainPersonalInfo?.personality === Personality.LEGAL_ENTITY;
  }

  handleBalanceRecordPrintClick() {
    return this.router.navigate(['customer/printBalanceRecord', this.currentCustomer.code]);
  }

  handleCreditReferenceClick() {
    this.router.navigate(['customer/creditReference', this.currentCustomer.code]);
  }
}
